import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { Link } from "react-router-dom";
import { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelectActions } from "./MultiSelectActions";

export interface EntityListProps {
  data: Array<{ id: string; [key: string]: any }>;
  columns: Array<{
    key: string;
    header: string;
    cell: (item: any) => ReactNode;
  }>;
  isLoading: boolean;
  emptyMessage?: string;
  basePath: string;
  isMultiSelectMode?: boolean;
  onToggleMultiSelect?: () => void;
  onToggleItemSelection?: (id: string) => void;
  isItemSelected?: (id: string) => boolean;
  onDeleteSelected?: () => void;
  onMergeSelected?: () => void;
  onMassUpdate?: () => void;
  selectedCount?: number;
  selectAll?: () => void;
  showCVIcon?: boolean;
  showActionsColumn?: boolean;
}

export const EntityList = ({
  data,
  columns,
  isLoading,
  emptyMessage = "No items found matching your search criteria.",
  basePath,
  isMultiSelectMode = false,
  onToggleMultiSelect,
  onToggleItemSelection,
  isItemSelected,
  onDeleteSelected,
  onMergeSelected,
  onMassUpdate,
  selectedCount = 0,
  selectAll,
  showCVIcon = false,
  showActionsColumn = true,
}: EntityListProps) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-sm shadow p-4 animate-pulse">
        {[1, 2, 3].map(i => (
          <div key={i} className="border-b border-gray-100 dark:border-gray-700 py-4">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          </div>
        ))}
      </div>
    );
  }

  // Only show empty message if not loading and data is truly empty (after initial fetch)
  if (!isLoading && data.length === 0) {
    return (
      <div className="text-center p-8 bg-white rounded-sm shadow">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-sm shadow overflow-hidden">
      {onToggleMultiSelect && (
        <div className="p-4 border-b">
          <MultiSelectActions
            isMultiSelectMode={isMultiSelectMode}
            onToggleMultiSelect={onToggleMultiSelect}
            onDeleteSelected={onDeleteSelected || (() => {})}
            onMergeSelected={onMergeSelected}
            onMassUpdate={onMassUpdate}
            selectedCount={selectedCount}
            selectAll={selectAll}
          />
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
        <TableHeader>
          <TableRow>
            {isMultiSelectMode && (
              <TableHead className="w-[50px]">
                <span className="sr-only">Select</span>
              </TableHead>
            )}
            {columns.map((column) => (
              <TableHead key={column.key}>{column.header}</TableHead>
            ))}
            {showActionsColumn && (
              <TableHead className="text-right">Actions</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id}>
              {isMultiSelectMode && onToggleItemSelection && (
                <TableCell>
                  <Checkbox
                    checked={isItemSelected?.(item.id) || false}
                    onCheckedChange={() => onToggleItemSelection(item.id)}
                    aria-label={`Select ${item.id}`}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell key={`${item.id}-${column.key}`}>
                  {column.cell(item)}
                </TableCell>
              ))}
              {showActionsColumn && (
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`${basePath}/${item.id}`}>
                        <Edit className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
};
